/**
 * Tope absoluto de sesión.
 *
 * Se mide contra el instante del login, NO contra la inactividad: recargar la
 * página, cambiar de pestaña o volver desde background no lo reinician. Vale
 * igual para operadores y clientes, en escritorio y en móvil.
 *
 * El ancla es `auth_time`, un claim que Cognito pone en los tokens con el
 * momento de la autenticación real y que conserva al refrescar. Por eso el
 * tope alcanza también a las sesiones que ya estaban abiertas cuando se
 * publicó este código: no hay nada que migrar ni que persistir en el login.
 */
export const SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * Marcador local de respaldo. Se ancla la primera vez que vemos una sesión y
 * cubre dos huecos de `auth_time`: que el claim no venga en el token, y que
 * Cognito lo moviera al refrescar (en cuyo caso el tope no vencería nunca).
 * Gana el más viejo de los dos, así que el respaldo sólo puede adelantar el
 * corte, nunca retrasarlo.
 */
export const SESSION_START_KEY = 'aguacate_session_start';

/**
 * Claves internas de @ng-idle. El wrapper `LocalStorage` de la librería
 * antepone `ng2Idle.` a cada clave.
 */
const IDLE_STORAGE_KEYS = ['ng2Idle.main.expiry', 'ng2Idle.main.idling'];

/**
 * Margen negativo tolerado entre el reloj del dispositivo y el del token.
 * Cubre redondeos y latencia; más que esto es un reloj atrasado de verdad.
 */
const CLOCK_TOLERANCE_MS = 60 * 1000;

/**
 * Desfasaje positivo a partir del cual dejamos de creerle al reloj del
 * dispositivo. Con un reloj sano, "ahora" cae entre la emisión del token y su
 * vencimiento (como mucho un par de horas). Seis horas ya no es un token
 * viejo: es el reloj del equipo corrido.
 */
const CLOCK_SANITY_MS = 6 * 60 * 60 * 1000;

/**
 * Devuelve el payload del token, tolerando las dos formas en que Amplify
 * expone la sesión: propiedades planas y los getters de CognitoUserSession.
 */
function readTokenPayload(session: any): any {
  if (!session) return null;
  return (
    session.accessToken?.payload ??
    session.idToken?.payload ??
    session.getAccessToken?.()?.payload ??
    session.getIdToken?.()?.payload ??
    null
  );
}

function readClaimMs(session: any, claim: string): number | null {
  const value = readTokenPayload(session)?.[claim];
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return value * 1000;
}

/** `auth_time` del token, en milisegundos. `null` si el claim no está. */
export function getAuthTimeMs(session: any): number | null {
  return readClaimMs(session, 'auth_time');
}

/**
 * "Ahora" confiable, en milisegundos.
 *
 * El tope no puede depender del reloj del dispositivo: un equipo adelantado
 * 13 h daría una sesión de 13 h recién emitida y dejaría al usuario en un
 * bucle —entra, lo echa, entra, lo echa— del que no puede salir solo.
 *
 * `iat` y `auth_time` los pone Cognito con el mismo reloj, así que cuando el
 * del dispositivo se va de rango caemos al del servidor. Se pierde precisión
 * (`iat` sólo avanza al refrescar el token) pero nunca se echa a nadie por
 * tener la hora mal puesta.
 */
export function resolveNowMs(session: any, deviceNow: number = Date.now()): number {
  const iat = readClaimMs(session, 'iat');
  if (iat === null) return deviceNow;

  const skew = deviceNow - iat;
  if (skew < -CLOCK_TOLERANCE_MS || skew > CLOCK_SANITY_MS) {
    return iat;
  }
  return deviceNow;
}

function readSessionStartMarker(): number | null {
  try {
    const raw = localStorage.getItem(SESSION_START_KEY);
    if (!raw) return null;
    const value = Number(raw);
    // Un marcador corrupto se ignora y se reescribe: preferimos perder el
    // ancla de respaldo antes que dejar al usuario en un logout perpetuo.
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null; // Safari en modo privado
  }
}

function writeSessionStartMarker(value: number): void {
  try {
    localStorage.setItem(SESSION_START_KEY, String(value));
  } catch {
    /* Safari en modo privado: seguimos con `auth_time` como único ancla */
  }
}

/**
 * Borra el rastro de @ng-idle. El dashboard ya no corta por inactividad —ni a
 * operadores ni a clientes—, pero un navegador puede arrastrar la expiración
 * del contador viejo, y el módulo retail todavía lo usa y la leería vencida.
 */
export function clearIdleAnchors(): void {
  try {
    for (const key of IDLE_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch {
    /* Safari en modo privado */
  }
}

/**
 * Limpia los anclajes de la sesión anterior. Se llama al iniciar sesión: sin
 * esto, entrar con credenciales nuevas sin haber cerrado la sesión previa
 * heredaba el marcador viejo y el tope cortaba la sesión recién abierta.
 */
export function clearSessionAnchors(): void {
  try {
    localStorage.removeItem(SESSION_START_KEY);
  } catch {
    /* Safari en modo privado */
  }
  clearIdleAnchors();
}

/**
 * Instante en que arrancó la sesión, en milisegundos. Ancla el marcador local
 * la primera vez que ve la sesión y devuelve el más viejo de los dos orígenes.
 */
export function resolveSessionStartMs(
  session: any,
  now: number = Date.now(),
): number | null {
  if (!session) return null;

  const authTime = getAuthTimeMs(session);
  let marker = readSessionStartMarker();

  if (marker === null) {
    marker = authTime !== null ? Math.min(authTime, now) : now;
    writeSessionStartMarker(marker);
  }

  return authTime !== null ? Math.min(authTime, marker) : marker;
}

/**
 * Antigüedad de la sesión en milisegundos. Nunca negativa: si el reloj del
 * dispositivo está atrasado respecto del de Cognito, cuenta como recién
 * abierta en lugar de arrojar un valor absurdo.
 */
export function getSessionAgeMs(
  session: any,
  deviceNow: number = Date.now(),
): number | null {
  // Todo se mide contra el mismo "ahora", incluido el marcador que se ancla
  // adentro de resolveSessionStartMs: mezclarlos con un reloj corrido daría
  // edades absurdas en cualquiera de las dos direcciones.
  const now = resolveNowMs(session, deviceNow);
  const start = resolveSessionStartMs(session, now);
  if (start === null) return null;
  return Math.max(0, now - start);
}

/** `true` si la sesión ya superó el tope de 12 h. */
export function isSessionExpiredByAge(
  session: any,
  deviceNow: number = Date.now(),
): boolean {
  const age = getSessionAgeMs(session, deviceNow);
  return age !== null && age >= SESSION_MAX_AGE_MS;
}

/** Milisegundos que le quedan a la sesión antes del tope. */
export function msUntilSessionExpiry(
  session: any,
  deviceNow: number = Date.now(),
): number | null {
  const age = getSessionAgeMs(session, deviceNow);
  if (age === null) return null;
  return Math.max(0, SESSION_MAX_AGE_MS - age);
}

/**
 * `exp` del idToken en milisegundos, con caída al accessToken.
 *
 * Se prefiere el idToken porque es el que `ApiRestInterceptor` manda en
 * `Authorization` y el que valida el backend. Cognito los emite juntos y con
 * el mismo vencimiento, así que el respaldo no cambia la respuesta; está para
 * las dos formas en que Amplify expone la sesión.
 */
function readIdTokenExpMs(session: any): number | null {
  const payload = session?.idToken?.payload ?? session?.getIdToken?.()?.payload;
  const value = payload?.['exp'];
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value * 1000;
  }
  return readClaimMs(session, 'exp');
}

/**
 * `true` si el token con el que se firman las peticiones ya venció.
 *
 * Sirve para distinguir "se cayó la sesión" de "el servidor rechazó esta
 * petición": un 401 con token vivo no es una sesión vencida, y tratarlo como
 * tal echa al usuario de una sesión que estaba perfectamente bien.
 *
 * Sin sesión, o sin `exp`, devuelve `true`: no hay con qué autenticarse.
 *
 * Mide contra el mismo "ahora" que el resto del archivo, así que un equipo
 * con el reloj adelantado no da por vencido un token que el servidor sigue
 * aceptando.
 */
export function isSessionTokenExpired(
  session: any,
  deviceNow: number = Date.now(),
): boolean {
  if (!session) return true;

  const exp = readIdTokenExpMs(session);
  if (exp === null) return true;

  return exp <= resolveNowMs(session, deviceNow);
}
